'use client';
import React, { Suspense } from 'react';
import { useBundleSelection } from '@/components/product/product-context';
import { findMatchingVariant } from '@/lib/bundles/utils';

// Helper to get the reference product for price difference calculation
function getReferenceProduct(selectedProduct, defaultProduct) {
  return selectedProduct || (defaultProduct && defaultProduct[0]);
}

// Cleans a product title down to a bare comparison key, e.g.
// "Original Mattress" -> "original". Used to match the dropdown's
// selected value back to a product, since option values are titles.
function titleKey(title) {
  return title
    .replace(/\s*mattress.*$/i, '')
    .replace(/\s*[^\w\s]+.*$/i, '')
    .trim()
    .toLowerCase();
}

function getSortedProducts(products, selectedOptionValue) {
  if (!selectedOptionValue) {
    return products;
  }

  return [...products].sort((a, b) => {
    const priceA =
      findMatchingVariant(
        a.product[0]?.shopifyProduct?.variants || [],
        selectedOptionValue
      )?.price.amount || 0;

    const priceB =
      findMatchingVariant(
        b.product[0]?.shopifyProduct?.variants || [],
        selectedOptionValue
      )?.price.amount || 0;

    return priceA - priceB;
  });
}

function getPriceDifferenceLabel({
  productNode,
  referenceProduct,
  selectedOptionValue,
  isSelected
}) {
  if (!selectedOptionValue || !referenceProduct || isSelected) {
    return '';
  }

  const selectedVariantPrice = findMatchingVariant(
    productNode?.shopifyProduct?.variants || [],
    selectedOptionValue
  )?.price.amount;

  const referenceVariantPrice = findMatchingVariant(
    referenceProduct?.shopifyProduct?.variants || [],
    selectedOptionValue
  )?.price.amount;

  if (selectedVariantPrice == null || referenceVariantPrice == null) {
    return '';
  }

  const priceDifference = selectedVariantPrice - referenceVariantPrice;

  if (priceDifference === 0) {
    return '';
  }

  const absDiff = Math.abs(priceDifference);
  return priceDifference > 0 ? `+$${absDiff}` : `-$${absDiff}`;
}

function ProductSelectorSelect({
  products,
  defaultProduct,
  selectedProduct,
  selectedOptionValue,
  onSelectedItemChange
}) {
  const referenceProduct = getReferenceProduct(selectedProduct, [
    defaultProduct
  ]);
  const sortedProducts = getSortedProducts(products, selectedOptionValue);
  const fallbackTitle =
    defaultProduct?.title?.replace(/\s*mattress$/i, '') || '';
  const selectedTitle = selectedProduct?.title;

  return (
    <select
      value={selectedTitle ?? ''}
      className="text-grey-text focus-visible:ring-blue body-r mt-2 block w-full cursor-pointer bg-white px-6 py-[14px] ring-1 ring-gray-300 ring-inset focus:ring-2 focus-visible:border focus-visible:ring-4"
      onChange={(e) => onSelectedItemChange(e.target.value)}
      data-cy="bundle-product-selector-dropdown"
    >
      {!selectedTitle && fallbackTitle && (
        <option value="" disabled>
          {fallbackTitle}
        </option>
      )}
      {sortedProducts.map((product, index) => {
        const productNode = product.product[0];
        const cleanedTitle = productNode?.title?.replace(/\s*mattress$/i, '');
        const isSelected = selectedProduct?.title === productNode?.title;
        const priceDifference = getPriceDifferenceLabel({
          productNode,
          referenceProduct,
          selectedOptionValue,
          isSelected
        });

        return (
          <option key={index} value={productNode?.title}>
            {cleanedTitle}
            {priceDifference !== '' && <>&nbsp;&nbsp;{priceDifference}</>}
          </option>
        );
      })}
    </select>
  );
}

/**
 * Reads the bundle selection, so it needs to be wrapped in a Suspense tag.
 */
function ResolvedProductSelector({ products, defaultProduct }) {
  const bundleState = useBundleSelection();

  function onSelectedItemChange(value) {
    const selectedBundleProduct = products.find(
      (product) => titleKey(product.product[0].title) === titleKey(value)
    );
    if (!selectedBundleProduct) return;

    bundleState.setSelectedProduct(
      selectedBundleProduct.product[0],
      selectedBundleProduct.default_secondary_attribute
    );
  }

  return (
    <ProductSelectorSelect
      products={products}
      defaultProduct={defaultProduct}
      selectedProduct={bundleState.selectedProduct}
      selectedOptionValue={bundleState.selectedOption?.value}
      onSelectedItemChange={onSelectedItemChange}
    />
  );
}

export default function ProductSelector({ block, sliceZoneIndex, lastBlock }) {
  const { label, products } = block.product_selector;
  const defaultBundleProduct = products.find(
    (product) => product.default_product
  );
  const defaultProduct = defaultBundleProduct?.product?.[0];

  return (
    <section
      id={`bundle-options-${sliceZoneIndex}`}
      className={`bg-parlor-green-95 my-4 ${lastBlock ? '' : 'mb-8'}`}
      data-cy="bundle-product-selector"
    >
      {label && <div className="sh12 mb-2 text-white">{label}</div>}
      <div className="text-crisp-white relative w-full cursor-pointer">
        <div className="relative mt-2 mb-6">
          <Suspense
            fallback={
              <ProductSelectorSelect
                products={products}
                defaultProduct={defaultProduct}
                onSelectedItemChange={() => {}}
              />
            }
          >
            <ResolvedProductSelector
              products={products}
              defaultProduct={defaultProduct}
            />
          </Suspense>
        </div>
      </div>
    </section>
  );
}
